import colors from "wwwroot/json/colors.json";
import { localize } from "@gtmhub/localization";
import { capitalize } from "@gtmhub/util";
import { IGridColumnDefinition } from "@gtmhub/shared/components/grid-filtering-bar/grid-columns/models";
import { getPersistedScreenSettings, setPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar/persisting/models";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { CustomFieldSortBySessionCountType, ICustomField, IGridCustomField } from "./models/models";
import { IFilterService } from "angular";
import { IGridSortingDefinition } from "@gtmhub/shared/components/grid-filtering-bar/grid-sorting/models";
import { IPlanningSession } from "@gtmhub/sessions";
import dayjs from "dayjs";

export const getDefaultPredefinedValueColor = (): string => {
  return colors.customFieldColors[0];
};

export const generateCustomFieldNameFromLabel = (label: string): string => {
  const prefixLength = 2;
  const prefix = generateRandomString(prefixLength) + "_";

  const name = prefix + replaceSpecialCharacters(label).toLowerCase();

  const maxNameLength = 12;
  const nameLength = name.charAt(maxNameLength - 1) === "_" ? maxNameLength - 1 : maxNameLength;

  return name.substr(0, nameLength);
};
// Replaces non supported characters from label into a random character (a-z)
// Adds _ instead of blank space
const replaceSpecialCharacters = (str: string): string => {
  const result = [];

  for (let i = 0; i < str.length; i++) {
    let current = str.charAt(i);

    if (current.match(/[^a-zA-Z0-9 ]/g)) {
      current = getRandomCharacter();
    }
    if (current === " ") {
      current = "_";
    }

    result.push(current);
  }

  return result.join("");
};

const generateRandomString = (length: number): string => {
  return Array.from({ length }, () => getRandomCharacter()).join("");
};

function getRandomCharacter(): string {
  const characters = "abcdefghijklmnopqrstuvwxyz";

  return characters.charAt(Math.floor(Math.random() * characters.length));
}

export const columnsFieldLabelMap = {
  label: "grid_col_label",
  visualizationKind: "grid_col_visualizationKind",
  targetTypes: "grid_col_targetTypes",
  sessions: "grid_col_sessions",
  dateCreated: "grid_col_dateCreated",
  uniqueKey: "grid_col_uniqueKey",
  description: "grid_col_description",
  lastModified: "grid_col_lastModified",
};

const defaultColWidth = {
  large: 180,
  medium: 175,
  small: 155,
  smallest: 50,
};

const minColumnWidth = {
  large: 180,
  medium: 150,
  small: 100,
};

const maxColumnWidth = {
  large: 450,
  small: 200,
};

export const defaultCustomFieldGHGridColumns = (): IGHGridColumn[] => [
  {
    fieldName: "label",
    width: defaultColWidth.large,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "targetTypes",
    width: defaultColWidth.medium,
    minWidth: minColumnWidth.medium,
    maxWidth: maxColumnWidth.small,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "visualizationKind",
    width: defaultColWidth.small,
    minWidth: minColumnWidth.small,
    maxWidth: maxColumnWidth.small,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "sessions",
    width: defaultColWidth.large,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "dateCreated",
    width: defaultColWidth.small,
    minWidth: minColumnWidth.small,
    maxWidth: maxColumnWidth.small,
    type: "date",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "uniqueKey",
    width: defaultColWidth.small,
    minWidth: minColumnWidth.small,
    maxWidth: maxColumnWidth.small,
    type: "alphabetic",
    isCustom: false,
    visible: false,
  },
  {
    fieldName: "description",
    width: defaultColWidth.large,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: false,
  },
  {
    fieldName: "lastModified",
    width: defaultColWidth.small,
    minWidth: minColumnWidth.small,
    maxWidth: maxColumnWidth.small,
    type: "date",
    isCustom: false,
    visible: false,
  },
  {
    fieldName: "actions",
    width: defaultColWidth.smallest,
    type: "dropdown",
    isCustom: false,
    visible: true,
  },
];

export const getCustomFieldGHGridColumns = (screenPath: string[]): IGHGridColumn[] => {
  const defaultColumns = defaultCustomFieldGHGridColumns();
  const persistedColumns = getPersistedScreenSettings(screenPath, "customFieldsGridColumns");

  if (persistedColumns && persistedColumns.length > 0) {
    return mergePersistedAndDefaultColumns(persistedColumns, defaultColumns);
  } else {
    setPersistedScreenSettings(screenPath, "customFieldsGridColumns", defaultColumns);
    return defaultColumns;
  }
};

const mergePersistedAndDefaultColumns = (persistedColumns: IGHGridColumn[], defaultColumns: IGHGridColumn[]): IGHGridColumn[] => {
  const persistedColumnsMap = {};

  persistedColumns.forEach((column) => {
    persistedColumnsMap[column.fieldName] = column;
  });

  return defaultColumns.map((column) => persistedColumnsMap[column.fieldName] || column);
};

export const localizeGHGridColName = function () {
  return function (column: IGHGridColumn): string {
    if (column.isCustom) {
      return column.label;
    }

    // enriching column field with prefix to generate unique keys for column fields
    // column fields are used as keys to get column labels
    const keyToFind: string = "grid_col_" + column.fieldName;

    return localize(keyToFind) || column.label;
  };
};

export const convertColumnToDefinition = (column: IGHGridColumn, $filter: IFilterService): IGridColumnDefinition => ({
  fieldName: column.fieldName,
  displayName: $filter<(column: IGHGridColumn) => string>("localizeGHGridColName")(column),
  trackingName: column.label,
  readOnly: column.fieldName === "label",
});

export const convertColumnToSortingDefinition = (column: IGHGridColumn, $filter: IFilterService): IGridSortingDefinition => ({
  fieldName: convertColumnToSortingDefinitionFieldName(column),
  displayName: $filter<(column: IGHGridColumn) => string>("localizeGHGridColName")(column),
  trackingName: column.label,
});

const convertColumnToSortingDefinitionFieldName = (column: IGHGridColumn): string => {
  return column.fieldName === "sessions" ? "operationalType" : column.fieldName === "uniqueKey" ? "name" : column.fieldName;
};

export const getMappedVisualizationKind = (key: string): string => {
  const keyMap = {
    "gtmhub.text": "text_field",
    "gtmhub.textarea": "text_area",
    "gtmhub.select": "drop_down_menu",
    "gtmhub.multiselect": "multi_select_list",
    "gtmhub.datepicker": "date_picker",
    "gtmhub.selectassignee": "user_team_selector",
    "gtmhub.numeric": "numeric",
  };

  return capitalize(localize(keyMap[key]));
};

export const getDistinctCustomFieldsById = (customFields: ICustomField[]): ICustomField[] => {
  const customFieldsMap: { [key: string]: ICustomField } = {};
  customFields.forEach((cf) => (customFieldsMap[cf._id] = cf));

  return Object.keys(customFieldsMap).map((id) => customFieldsMap[id]);
};

export const sortCustomFieldsBySessionCount = (customFields: IGridCustomField[], sortBy: CustomFieldSortBySessionCountType): IGridCustomField[] => {
  if (!customFields || customFields.length === 0) {
    return [];
  }

  const noSessionTargetTypes = new Set(["user", "team", "task"]);

  // Reorder custom fields without sessions to the bottom of the list
  const customFieldsAllSessions = [];
  const customFieldsWithSessions = [];
  const customFieldsWithoutSessions = [];

  customFields.forEach((field) => {
    if (noSessionTargetTypes.has(field.targetTypes)) {
      customFieldsWithoutSessions.push(field); // For types "user", "team", "task"
    } else if (field.sessions.length === 0) {
      customFieldsAllSessions.push(field);
    } else {
      customFieldsWithSessions.push(field);
    }
  });

  if (sortBy === "operationalType") {
    const sortByLengthAsc = (a: IGridCustomField, b: IGridCustomField) => a.sessions.length - b.sessions.length;

    return [...customFieldsWithSessions.sort(sortByLengthAsc), ...customFieldsAllSessions.sort(sortByLengthAsc), ...customFieldsWithoutSessions.sort(sortByLengthAsc)];
  } else {
    const sortByLengthDesc = (a: IGridCustomField, b: IGridCustomField) => b.sessions.length - a.sessions.length;

    return [...customFieldsAllSessions.sort(sortByLengthDesc), ...customFieldsWithSessions.sort(sortByLengthDesc), ...customFieldsWithoutSessions.sort(sortByLengthDesc)];
  }
};

export const findSessionsByIds = (sessions: IPlanningSession[], targetIds: string[]): IPlanningSession[] => {
  return sessions.reduce((targetSessions, session) => {
    if (targetIds.includes(session.id)) {
      targetSessions.push(session);
    }
    return targetSessions;
  }, []);
};

export const reorderInitialCustomFields = (customFields: ICustomField[], lastUpdated: string): ICustomField[] => {
  customFields = angular.copy(customFields);
  if (!customFields || customFields.length === 0) {
    return [];
  }
  if (!lastUpdated && customFields) {
    return [...customFields.sort((a, b) => a.orderId - b.orderId)];
  }
  const oldCustomFields = customFields.filter((cf) => dayjs(cf.dateCreated).isBefore(lastUpdated)).sort((a, b) => a.orderId - b.orderId);
  const newCustomFields = customFields.filter((cf) => dayjs(cf.dateCreated).isSameOrAfter(lastUpdated)).sort((a, b) => a.orderId - b.orderId);
  return [...oldCustomFields, ...newCustomFields];
};

export const checkIfCustomFieldIsNew = (item: ICustomField, dateToCompare: string): boolean => {
  if (!dateToCompare) {
    return true;
  }
  return dayjs(item.dateCreated).isSameOrAfter(dateToCompare);
};
